import React, { useEffect, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { PublicNav } from "@/components/PublicChrome";
import { Button } from "@/components/ui/button";
import { PasswordInput } from "@/components/PasswordInput";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { api, apiErr } from "@/lib/api";

import { ShieldCheck, Loader2, XCircle } from "lucide-react";

export default function SetPassword() {
  const [params] = useSearchParams();
  const token = params.get("token");
  const [info, setInfo] = useState(null);
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!token) { setInfo({ valid: false }); return; }
    api.get(`/auth/token-info?token=${encodeURIComponent(token)}`).then((r) => setInfo(r.data)).catch(() => setInfo({ valid: false }));
  }, [token]);

  const submit = async (e) => {
    e.preventDefault();
    if (password !== confirm) { toast.error("Passwords don't match"); return; }
    setBusy(true);
    try {
      await api.post("/auth/set-password", { token, password });
      toast.success("Password set — welcome to KOHC!");
      window.location.href = "/app/courses";
    } catch (err) { toast.error(apiErr(err.response?.data?.detail)); setBusy(false); }
  };

  return (
    <div className="min-h-screen bg-cream font-body">
      <PublicNav />
      <div className="grid place-items-center px-6 py-16">
        <div className="w-full max-w-md bg-cream-card border border-cream-deep rounded-3xl p-8 md:p-10">
          {info === null && <div className="text-center py-8"><Loader2 className="h-10 w-10 text-brand mx-auto animate-spin" /></div>}
          {info && info.valid === false && (
            <div className="text-center" data-testid="set-password-invalid">
              <XCircle className="h-14 w-14 text-rag-red mx-auto" />
              <h1 className="text-2xl font-bold text-brand mt-4">Link invalid or expired</h1>
              <p className="text-muted-foreground mt-2">This set-password link is no longer valid. Please ask your head office to re-invite you.</p>
              <Link to="/login"><Button className="mt-6 rounded-full bg-brand text-white h-11 px-8">Go to login</Button></Link>
            </div>
          )}
          {info && info.valid && (
            <>
              <ShieldCheck className="h-10 w-10 text-coral" />
              <h1 className="text-2xl font-bold text-brand mt-4">Welcome, {info.name?.split(" ")[0] || "there"}</h1>
              <p className="text-muted-foreground mt-2">Set a password for <span className="font-medium text-foreground">{info.email}</span> to access your KOHC dashboard.</p>
              <form onSubmit={submit} className="space-y-4 mt-6">
                <div><Label>New password</Label><PasswordInput testId="set-password-input" required minLength={6} value={password} onChange={(e) => setPassword(e.target.value)} /></div>
                <div><Label>Confirm password</Label><PasswordInput testId="set-password-confirm" required minLength={6} value={confirm} onChange={(e) => setConfirm(e.target.value)} /></div>
                <Button data-testid="set-password-submit" type="submit" disabled={busy} className="w-full h-11 rounded-full bg-coral hover:bg-coral-hover text-white">
                  {busy ? "Setting…" : "Set password & sign in"}
                </Button>
              </form>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
